import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Inbox } from 'lucide-react';
import { useState } from 'react';
import { useBrand } from '@/contexts/BrandContext';
import { getCategories, getPopularArticles } from '@/services/api';
import { CategoryCard } from '@/components';
import { ErrorState, EmptyState, LoadingSpinner } from '@airclou/ui';

export function HomePage() {
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const { data: popularArticles } = useQuery({
    queryKey: ['popularArticles'],
    queryFn: () => getPopularArticles(5),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{
          backgroundColor: brand?.hero_image_url
            ? undefined
            : 'var(--hero-bg, #f8fafc)',
        }}
      >
        {/* Hero Background Image */}
        {brand?.hero_image_url && (
          <>
            <img
              src={brand.hero_image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
              aria-hidden="true"
            />
          </>
        )}
        <div className="mx-auto max-w-[1120px] px-4 md:px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <h1
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: brand?.hero_image_url ? '#ffffff' : undefined }}
            >
              {brand?.hero_title || 'How can we help?'}
            </h1>
            <form onSubmit={handleSearch} className="mt-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="search"
                  placeholder={brand?.hero_subtitle || 'Search for answers...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-14 pl-12 pr-4 rounded-xl border bg-background text-foreground text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                />
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12">
        <div className="mx-auto max-w-[1120px] px-4 md:px-6">
          {categoriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : categoriesError ? (
            <ErrorState
              description="Failed to load categories. Please try again."
              action={
                <button
                  onClick={() => refetchCategories()}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              }
            />
          ) : categories && categories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Inbox}
              title="No help content yet"
              description="Check back later for articles and guides."
            />
          )}
        </div>
      </section>

      {/* Popular Articles Section */}
      {popularArticles && popularArticles.length > 0 && (
        <section className="py-12 bg-muted/30">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <h2 className="text-xl font-semibold mb-6">Popular articles</h2>
            <ul className="space-y-2">
              {popularArticles.map((article) => {
                const href = `/articles/${article.slug}`;

                return (
                  <li key={article.id}>
                    <Link
                      to={href}
                      className="block p-3 rounded-lg hover:bg-muted transition-colors"
                    >
                      <span className="text-foreground hover:text-primary">
                        {article.title}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
